import { ILayoutProps } from "./Layout/types";
import { IOutputProps } from "./Output/types";
import {
  IAction,
  IBase,
  IFile,
  IGetValue,
  IInput,
  ILayout,
  IList,
  IMetadata,
  IModel,
  IName,
  INumber,
  IRange,
  IResult,
  ITemplate,
  IUpload,
  IValue,
} from "./types";
import { IInputProps } from "./Input/types";
import { IInputValidateProps, ILayoutValidateProps } from "./Validate";
import { applyFunction, evaluate } from "./action";
import { get, isArray } from "lodash";
import { buildPath } from "./common";
import { checkValue as checkNumber } from "./Input/Number";
import { checkValue as checkFile } from "./Input/File";
import { checkValue as checkRange } from "./Input/Range";
import { IRadioOptionProps } from "./Input/Radio";

/**
 * Returns a name and path list of all of the currently visible items.
 * @param item
 * @param model
 * @returns
 */
export const getVisible = (item: Array<IBase> | IBase, model: IModel): Array<IName> => {
  const visible = [] as Array<IName>;
  const getValue = ((p) => get(model, p)) as IGetValue;
  const check = (v: IBase, l?: { name: IName; index: number }): void => {
    const path = l ? buildPath(l.name, l.index, v.name) : v.name;
    const gvw = l ? (((p: IName) => getValue(path)) as IGetValue) : getValue;
    if (v.hidden && evaluate(model, v.hidden, gvw)) {
      return;
    }
    visible.push(path);
    switch (v.type) {
      case "list":
        const list = v as IList;
        const items = (gvw(list.name) as Array<any>).map((_a, n) => n);
        for (const n of items) {
          for (const i of list.items) {
            check(i, { name: list.name, index: n });
          }
        }
        return;
      case "group":
      case "anchors":
      case "tabs":
      case "tab":
      case "explorer":
        const layout = v as ILayout<IBase>;
        for (const i of layout.items) {
          check(i, l);
        }
        return;
      case "label":
      case "image":
      case "number":
      case "checkbox":
      case "text":
      case "file":
      case "select":
      case "range":
      case "radio":
      case "location":
      case "location_matrix":
      case "calculate":
      default:
        return;
    }
  };
  (isArray(item) ? item : [item]).forEach((i) => check(i));
  return visible;
};

/**
 * Recursively checks whether this and child components are valid.
 * @param item
 * @param model
 * @param result
 * @returns
 */
export const isInvalid = (
  item: Array<IBase> | IBase,
  model: IModel,
  result?: IResult,
  metadata?: IMetadata
): boolean => {
  const getValue = ((p) => get(model, p)) as IGetValue;
  const faults = metadata?.faults;
  const loading = metadata?.loading;
  const checkFaults = (name: IName, l?: { name: IName; index: number }) => {
    const keys = l ? [buildPath(l.name, l.index, name), name] : [name];
    const hasFault = faults ? keys.some((key) => key in faults) : false;
    const hasLoading = loading ? keys.some((key) => loading.includes(key)) : false;

    if (loading) {
      return hasFault && !hasLoading;
    } else {
      return hasFault;
    }
  };
  const check = (v: IBase, l?: { name: IName; index: number }): boolean => {
    const gvw = l ? (((p: IName) => getValue(buildPath(l.name, l.index, p))) as IGetValue) : getValue;
    if (v.hidden && evaluate(model, v.hidden, gvw)) {
      return false;
    }
    switch (v.type) {
      case "list":
        const list = v as IList;
        if (list.validate && !evaluate(model, list.validate, gvw, result)) {
          return true;
        }
        const itemIndices = (gvw(list.name) as Array<any>).map((_a, n) => n);
        for (const n of itemIndices) {
          for (const i of list.items) {
            if (check(i, { name: list.name, index: n })) {
              return true;
            }
          }
        }
        return false;
      case "group":
      case "anchors":
      case "tabs":
      case "tab":
      case "explorer":
        const layout = v as ILayout<IBase>;
        if (layout.validate && !evaluate(model, layout.validate, gvw, result)) {
          return true;
        }
        for (const i of layout.items) {
          if (check(i, l)) {
            return true;
          }
        }
        return false;
      case "label":
      case "image":
        const label = v as ILayout<IBase>;
        if (label.validate && !evaluate(model, label.validate, gvw, result)) {
          return true;
        }
        return false;
      case "number":
        const inputNumber = v as INumber;
        if (
          (inputNumber.validate && !evaluate(model, inputNumber.validate, gvw, result)) ||
          checkNumber(inputNumber, gvw(inputNumber.name, model) as string) ||
          checkFaults(inputNumber.name, l)
        ) {
          return true;
        }
        return false;
      case "file":
        const inputFile = v as IFile;
        if (
          (inputFile.validate && !evaluate(model, inputFile.validate, gvw, result)) ||
          checkFile(inputFile, gvw(inputFile.name, model) as IUpload) ||
          checkFaults(inputFile.name, l)
        ) {
          return true;
        }
        return false;
      case "range":
        const inputRange = v as IRange;

        const min = typeof inputRange.min === 'object' ? applyFunction(model, inputRange.min, getValue) as number : inputRange.min;
        const max = typeof inputRange.max === 'object' ? applyFunction(model, inputRange.max, getValue) as number : inputRange.max;

        if (
          (inputRange.validate && !evaluate(model, inputRange.validate, gvw, result)) ||
          checkRange([min, max], gvw(inputRange.name, model) as [number, number]) ||
          checkFaults(inputRange.name, l)
        ) {
          return true;
        }
        return false;
      case "checkbox":
      case "text":
      case "select":
      case "radio":
      case "location":
      case "location_matrix":
      case "calculate":
        const input = v as IInput;
        if ((input.validate && !evaluate(model, input.validate, gvw, result)) || checkFaults(input.name, l)) {
          return true;
        }
        return false;
      default:
        return false;
    }
  };
  return (isArray(item) ? item : [item]).find((i) => check(i)) !== undefined;
};

/**
 * Returns true if the component and any children should not be rendered.
 *
 * @param props
 * @returns
 */
export const doHidden = (props: IInputProps | ILayoutProps | IOutputProps): boolean => {
  const { template, model, mutators } = props;
  const result = "result" in props ? props.result : undefined;
  const { getValue, isHidden } = mutators;

  return Boolean(
    (isHidden && isHidden(template.name)) || (template.hidden && evaluate(model, template.hidden, getValue, result))
  );
};

/**
 * Returns true if this component failed validation.
 *
 * @param props
 * @returns
 */
export const doInvalid = (props: IInputProps | ILayoutProps): boolean => {
  const { template, model, mutators } = props;
  const disabled = doDisabled(props);
  const result = "result" in props ? props.result : undefined;
  const { getValue } = mutators;

  return Boolean(!disabled && template.validate && !evaluate(model, template.validate, getValue, result));
};

/**
 * Returns true if this component is disabled.
 *
 * @param props
 * @returns
 */
export const doDisabled = (props: IInputProps | ILayoutProps | IRadioOptionProps): boolean => {
  const { template, model, mutators } = props;
  const { getValue } = mutators;

  if (typeof template.disable === 'boolean') return template.disable

  return Boolean(template.disable && evaluate(model, template.disable, getValue));
};

/**
 * Returns the generated default value if default action is specified in the template.
 * @param props
 * @returns
 */
export const doDefault = (props: IInputValidateProps | ILayoutValidateProps): IValue | undefined => {
  const template = props.template as IInput;
  if (!template.default) {
    return;
  }
  const { model, mutators } = props as IInputValidateProps;
  const { getValue } = mutators;
  return template.default ? (applyFunction(model, template.default, getValue) as IValue) : undefined;
};

export const renderDescription = (description: string | IAction, model: IModel, result?: IResult) => {
  if (description) {
    if (typeof description === 'string') {
      return description
    }

    try {
      return applyFunction(model, description, get, result) as string
    } catch (error) {
      console.error("Error rendering description", {description, error});
      return undefined
    }
  }
  return undefined
}

/**
 * Use Tableau10 as chart color
 */
export const COLORWAY: string[] = [
  "#4e79a7",
  "#f28e2b",
  "#e15759",
  "#76b7b2",
  "#59a14f",
  "#edc948",
  "#b07aa1",
  "#ff9da7",
  "#9c755f",
  "#bab0ac",
];

/**
 * Check layout type
 */
export const isLayoutType = (type: any) => ["group", "tabs", "tab", "explorer", "anchors"].includes(type);

export const getInitialOutputState = (template: ITemplate) => {
  const getOutputStateForOutputs = (outputs: Array<ILayout<IBase>>) => {
    let outputState: Record<string, any> = {};

    for (const output of outputs as any) {
      if (output.name && output.value !== undefined) {
        outputState[output.name] = output.value;
      }
  
      if (output.items) {
        const innerOutputState = getOutputStateForOutputs(output.items)
  
        outputState = { ...outputState, ...innerOutputState }
      }
    }

    return outputState
  }

  return getOutputStateForOutputs(template.outputs);
}
